// Google Drive API utilities using direct fetch calls (similar to Python googleapiclient approach)

// Helper to find or create a folder and return its ID
export const findOrCreateFolder = async (folderName, parentFolderId = null, accessToken) => {
  if (!accessToken) {
    throw new Error("Access token is required for Google Drive operations");
  }
  
  let query = `mimeType='application/vnd.google-apps.folder' and name='${folderName}' and trashed=false`;
  if (parentFolderId) {
    query += ` and '${parentFolderId}' in parents`;
  } else {
    query += " and 'root' in parents";
  }

  try {
    // Search for existing folder - equivalent to drive_service.files().list()
    const searchResponse = await fetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name)`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!searchResponse.ok) {
      const errorData = await searchResponse.json();
      throw new Error(`Failed to search for folder: ${errorData.error?.message || searchResponse.statusText}`);
    }

    const searchResult = await searchResponse.json();
    
    if (searchResult.files && searchResult.files.length > 0) {
      console.log(`Folder '${folderName}' found with ID: ${searchResult.files[0].id}`);
      return searchResult.files[0].id;
    } else {
      console.log(`Folder '${folderName}' not found, creating...`);
      
      // Create new folder - equivalent to drive_service.files().create()
      const fileMetadata = {
        name: folderName,
        mimeType: "application/vnd.google-apps.folder",
      };
      if (parentFolderId) {
        fileMetadata.parents = [parentFolderId];
      }

      const createResponse = await fetch('https://www.googleapis.com/drive/v3/files?fields=id', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(fileMetadata)
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(`Failed to create folder: ${errorData.error?.message || createResponse.statusText}`);
      }

      const createResult = await createResponse.json();
      console.log(`Folder '${folderName}' created with ID: ${createResult.id}`);
      return createResult.id;
    }
  } catch (error) {
    console.error(`Error finding or creating folder '${folderName}':`, error);
    throw error;
  }
};

// Helper to check if a file already exists in a folder
export const findExistingFile = async (fileName, folderId, accessToken) => {
  if (!accessToken) {
    throw new Error("Access token is required for Google Drive operations");
  }

  let query = `name='${fileName}' and trashed=false`;
  if (folderId) {
    query += ` and '${folderId}' in parents`;
  } else {
    query += " and 'root' in parents";
  }

  try {
    // Search for existing file
    const searchResponse = await fetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name,mimeType)`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!searchResponse.ok) {
      const errorData = await searchResponse.json();
      throw new Error(`Failed to search for file: ${errorData.error?.message || searchResponse.statusText}`);
    }

    const searchResult = await searchResponse.json();
    
    if (searchResult.files && searchResult.files.length > 0) {
      console.log(`Found existing file '${fileName}' with ID: ${searchResult.files[0].id}`);
      return searchResult.files[0];
    }
    
    console.log(`No existing file found for '${fileName}'`);
    return null;
  } catch (error) {
    console.error(`Error searching for file '${fileName}':`, error);
    throw error;
  }
};

// Function to upload a file to Google Drive (or use existing file if it already exists)
export const uploadFileToDrive = async (fileBlob, fileName, mimeType, folderId, accessToken) => {
  if (!accessToken) {
    throw new Error("Access token is required for Google Drive operations");
  }

  try {
    // First, check if a file with the same name already exists
    const existingFile = await findExistingFile(fileName, folderId, accessToken);
    
    if (existingFile) {
      console.log(`Using existing file '${fileName}' with ID: ${existingFile.id} (skipped upload)`);
      
      // Ensure the existing file has the same permissions (world readable)
      try {
        const permissionResponse = await fetch(`https://www.googleapis.com/drive/v3/files/${existingFile.id}/permissions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            role: "reader",
            type: "anyone",
          })
        });

        if (!permissionResponse.ok) {
          // Permission might already exist, which is fine
          console.log(`Permissions may already be set for file ${existingFile.id}`);
        } else {
          console.log(`File ${existingFile.id} made world shareable.`);
        }
      } catch (permissionError) {
        console.warn(`Could not set permissions for existing file ${existingFile.id}:`, permissionError);
        // Don't throw - using existing file is still better than uploading duplicate
      }
      
      return existingFile;
    }

    // File doesn't exist, proceed with upload
    console.log(`File '${fileName}' not found, uploading new file...`);
    
    const metadata = {
      name: fileName,
      mimeType: mimeType,
      parents: folderId ? [folderId] : [],
    };

    const form = new FormData();
    form.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
    form.append("file", fileBlob);

    // Upload file - equivalent to drive_service.files().create()
    const response = await fetch("https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart", {
      method: "POST",
      headers: new Headers({ Authorization: `Bearer ${accessToken}` }),
      body: form,
    });

    if (!response.ok) {
      const errorDetails = await response.json();
      console.error("Drive upload failed:", errorDetails);
      throw new Error(`Drive upload failed: ${errorDetails.error?.message || response.statusText}`);
    }
    const result = await response.json();
    console.log("File uploaded successfully to Drive:", result);

    // Set permission to world shareable (reader) - equivalent to drive_service.permissions().create()
    const permissionResponse = await fetch(`https://www.googleapis.com/drive/v3/files/${result.id}/permissions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        role: "reader",
        type: "anyone",
      })
    });

    if (!permissionResponse.ok) {
      const permissionError = await permissionResponse.json();
      console.error("Failed to set file permissions:", permissionError);
      // Don't throw here - file is uploaded, just permissions failed
    } else {
      console.log(`File ${result.id} made world shareable.`);
    }
    
    return result;
  } catch (error) {
    console.error("Error uploading file to Drive:", error);
    throw error;
  }
};
